import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ShieldOff } from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { ProgressBar } from "../components/ui/ProgressBar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import { fmtNumber, fmtRelative } from "../lib/format";

const PAGE_SIZE = 25;

type BlockScope = "read" | "write" | "all";

type UserSummary = {
  user_id: string;
  last_seen_at: number;
  foods_created: number;
  reports_submitted: number;
  request_count: number;
  lookup_count: number;
  suggest_count: number;
  image_scan_count: number;
  barcode_lookup_count: number;
  search_count: number;
  read_count: number;
  report_count: number;
  is_blocked: boolean;
  block_scope: BlockScope | null;
};

type UsersResponse = {
  items: UserSummary[];
  total: number;
  page: number;
  pageSize: number;
};

type QuotaOverride = {
  user_id: string;
  daily_lookup_max: number | null;
  daily_suggest_max: number | null;
  reason: string | null;
  set_by: string;
  set_at: number;
};

type UserDetailResponse = UserSummary & {
  quota: {
    override: QuotaOverride | null;
    today: {
      lookup_count_today: number;
      suggest_count_today: number;
      quota_reset_at: number;
    };
    effective: {
      daily_lookup_max: number;
      daily_suggest_max: number;
    };
  };
};

type SearchLogRow = {
  id: string;
  user_id: string | null;
  ip_hash: string | null;
  query_raw: string;
  query_norm: string;
  resolved: 0 | 1;
  source: string | null;
  food_id: string | null;
  llm_called: 0 | 1;
  // Tagged failure mode when resolved=0; always null when resolved=1.
  // See services/lookup.ts → MissReason for the enum.
  miss_reason: string | null;
  created_at: number;
};
type SearchesResponse = {
  items: SearchLogRow[];
  next_cursor: number | null;
  limit: number;
};

/**
 * Plain-English tooltip for a search_log.miss_reason. Keep this in sync with
 * `MissReason` in src/services/lookup.ts. Unknown reasons fall through to
 * the raw tag — better to show "llm_rate_limited" than to silently drop it
 * if the backend ships a new reason before the admin redeploys.
 */
function describeMissReason(reason: string): string {
  switch (reason) {
    case "cascade_only":
      return "All free sources missed and no LLM is configured.";
    case "llm_not_a_food":
      return "LLM returned the empty sentinel — model thinks the input isn't a food, or bailed on a non-Latin-script word it didn't recognize.";
    case "llm_http_error":
      return "OpenRouter returned a non-2xx response (auth, rate limit, model unavailable).";
    case "llm_api_error":
      return "OpenRouter returned 200 with an error envelope — usually model overloaded or content-filtered.";
    case "llm_empty_response":
      return "OpenRouter response had no message content.";
    case "llm_bad_json":
      return "Model output didn't parse as JSON.";
    case "llm_timeout":
      return "OpenRouter didn't respond within the LLM timeout (8s).";
    case "llm_fetch_error":
      return "Network error reaching OpenRouter.";
    case "llm_shape_rejected":
      return "LLM returned a candidate but it failed the shape gate (likely non-Latin-script ingredient or too many).";
    case "llm_persist_failed":
      return "LLM gave a valid candidate but the D1 write failed.";
    default:
      return reason;
  }
}

/**
 * Compact "what is this user mostly doing" summary for the list table.
 * Returns the highest-count activity bucket with its share — e.g.
 * "barcode · 64%" or "search · 42%". When a user has zero bucketed
 * activity (read-only or just-blocked), returns "—". The detailed
 * per-bucket breakdown still lives on the Manage panel, this is the
 * glance for the list view.
 */
function topActivity(u: UserSummary): string {
  const buckets: { key: string; value: number }[] = [
    { key: "search", value: u.search_count },
    { key: "lookup", value: u.lookup_count },
    { key: "suggest", value: u.suggest_count },
    { key: "barcode", value: u.barcode_lookup_count },
    { key: "scan", value: u.image_scan_count },
    { key: "read", value: u.read_count },
    { key: "report", value: u.report_count },
  ];
  const total = buckets.reduce((a, b) => a + b.value, 0);
  if (total === 0) return "—";
  const top = buckets.reduce((a, b) => (b.value > a.value ? b : a));
  if (top.value === 0) return "—";
  const share = Math.round((top.value / total) * 100);
  return `${top.key} · ${share}%`;
}

/**
 * Users tab. Lists everyone we've stamped a verified Firebase uid on (via
 * food creation or report submission), with their activity counts and
 * block state. Click a row to open the detail/block panel.
 *
 * Why we don't have a real "users" table: identity is owned by Firebase.
 * This view is a derived projection from foods + food_reports. A user
 * who only reads the cache won't appear here — only writers do.
 */
export function UsersPage() {
  const [raw, setRaw] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw.trim()), 250);
    return () => clearTimeout(t);
  }, [raw]);

  useEffect(() => {
    setPage(1);
  }, [debounced]);

  const path = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    if (debounced) params.set("q", debounced);
    return `/v1/admin/users?${params.toString()}`;
  }, [debounced, page]);

  const { data, isLoading, error, isFetching } = useQuery<UsersResponse>({
    queryKey: ["admin-users", path],
    queryFn: () => api<UsersResponse>(path),
    placeholderData: (prev) => prev,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
          Users
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
          Firebase users who have made any authed request. "Lookups" and
          "Suggests" are the cost-relevant buckets — high counts on either
          mean a uid is burning LLM/upstream budget. Block bad actors at
          the gateway; blocked requests get a 403 BLOCKED before any handler
          runs.
        </p>
      </header>

      <div className="max-w-md">
        <Input
          type="search"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="filter by uid"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {error && (
        <Callout title="Failed to load" variant="error">
          {(error as Error).message}
        </Callout>
      )}

      {isLoading && !data ? (
        <Skeleton />
      ) : !data || data.items.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-gray-500 dark:text-gray-500">
            {debounced
              ? "No users match."
              : "No users yet — nobody has made an authed request."}
          </p>
        </Card>
      ) : (
        <>
          <TableRoot>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Last seen</TableHeaderCell>
                    <TableHeaderCell className="text-right" title="Total authed requests across all endpoints">
                      Requests
                    </TableHeaderCell>
                    <TableHeaderCell>Top activity</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell className="font-mono text-xs">
                        {u.user_id}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 dark:text-gray-400">
                        {fmtRelative(u.last_seen_at)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtNumber(u.request_count)}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600 dark:text-gray-400">
                        {topActivity(u)}
                      </TableCell>
                      <TableCell>
                        {u.is_blocked ? (
                          <Badge variant="error">
                            blocked · {u.block_scope}
                          </Badge>
                        ) : (
                          <Badge variant="neutral">active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="secondary"
                          onClick={() => setSelectedUid(u.user_id)}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableRoot>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={data.total}
            from={(page - 1) * PAGE_SIZE + 1}
            to={(page - 1) * PAGE_SIZE + data.items.length}
            busy={isFetching}
            onChange={setPage}
          />
        </>
      )}

      {selectedUid && (
        <UserDetail
          uid={selectedUid}
          onClose={() => setSelectedUid(null)}
        />
      )}
    </div>
  );
}

function UserDetail({ uid, onClose }: { uid: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<UserDetailResponse>({
    queryKey: ["admin-user", uid],
    queryFn: () =>
      api<UserDetailResponse>(`/v1/admin/users/${encodeURIComponent(uid)}`),
  });

  const [scope, setScope] = useState<BlockScope>("write");
  const [reason, setReason] = useState("");

  const block = useMutation({
    mutationFn: () =>
      api(`/v1/admin/users/${encodeURIComponent(uid)}/block`, {
        method: "POST",
        body: JSON.stringify({ scope, reason: reason.trim() || null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-user", uid] });
    },
  });

  const unblock = useMutation({
    mutationFn: () =>
      api(`/v1/admin/users/${encodeURIComponent(uid)}/block`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-user", uid] });
    },
  });

  return (
    <Card>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
            User detail
          </h2>
          <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-500">
            {uid}
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>

      {isLoading && <p className="text-sm">Loading…</p>}
      {error && (
        <Callout title="Failed to load user" variant="error">
          {(error as Error).message}
        </Callout>
      )}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <Stat label="Requests" value={fmtNumber(data.request_count)} />
            <Stat label="Last seen" value={fmtRelative(data.last_seen_at)} />
            <Stat label="Foods created" value={fmtNumber(data.foods_created)} />
            <Stat
              label="Status"
              value={
                data.is_blocked ? `blocked · ${data.block_scope}` : "active"
              }
              tone={data.is_blocked ? "err" : "ok"}
            />
          </div>

          {/* Full per-feature usage breakdown. Lookups/Suggests/Scans are the
              cost-relevant buckets (each implies an LLM or vision call);
              Searches/Reads/Barcodes are cache-or-cascade traffic. Reports
              are moderation-queue submissions. */}
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4 lg:grid-cols-7">
            <Stat label="Search" value={fmtNumber(data.search_count)} />
            <Stat label="Lookup" value={fmtNumber(data.lookup_count)} />
            <Stat label="Suggest" value={fmtNumber(data.suggest_count)} />
            <Stat label="Barcode" value={fmtNumber(data.barcode_lookup_count)} />
            <Stat label="Scan" value={fmtNumber(data.image_scan_count)} />
            <Stat label="Reads" value={fmtNumber(data.read_count)} />
            <Stat
              label="Reports"
              value={fmtNumber(data.reports_submitted || data.report_count)}
            />
          </div>

          <QuotaPanel uid={uid} quota={data.quota} />

          <UserSearches uid={uid} />

          <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-900">
            {data.is_blocked ? (
              <div>
                <p className="mb-3 text-sm">
                  This user is currently blocked from{" "}
                  <span className="font-semibold">{data.block_scope}</span>.
                  Unblock to restore access.
                </p>
                <Button
                  variant="primary"
                  isLoading={unblock.isPending}
                  onClick={() => unblock.mutate()}
                >
                  <ShieldOff className="mr-1.5 size-4" /> Unblock
                </Button>
                {unblock.error && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-500">
                    {(unblock.error as Error).message}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium">Block this user</p>
                <div>
                  <Label htmlFor="block-scope">Scope</Label>
                  <select
                    id="block-scope"
                    value={scope}
                    onChange={(e) => setScope(e.target.value as BlockScope)}
                    className="mt-1.5 block w-full max-w-xs rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-50"
                  >
                    <option value="write">
                      write — block POSTs (lookup, suggest, search, report)
                    </option>
                    <option value="all">
                      all — block both reads and writes
                    </option>
                    <option value="read">
                      read — block GETs (foods list, sync, ingredients)
                    </option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="block-reason">Reason (optional)</Label>
                  <Input
                    id="block-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. spamming bad reports"
                    className="mt-1.5"
                  />
                </div>
                <Button
                  variant="destructive"
                  isLoading={block.isPending}
                  onClick={() => block.mutate()}
                >
                  Block
                </Button>
                {block.error && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-500">
                    {(block.error as Error).message}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Today's usage vs. the per-uid daily quota, plus controls to set/clear an
 * override. Counters mirror what `requireWithinQuota` reads on the hot path,
 * so the gauge here is what would actually gate the next request.
 *
 * Override === null means the uid is on env defaults; we still render the
 * effective number for clarity (admin shouldn't have to guess).
 */
function QuotaPanel({
  uid,
  quota,
}: {
  uid: string;
  quota: UserDetailResponse["quota"];
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [lookupMax, setLookupMax] = useState<string>(
    quota.override?.daily_lookup_max?.toString() ?? "",
  );
  const [suggestMax, setSuggestMax] = useState<string>(
    quota.override?.daily_suggest_max?.toString() ?? "",
  );
  const [reason, setReason] = useState<string>(quota.override?.reason ?? "");

  const setQuota = useMutation({
    mutationFn: () =>
      api(`/v1/admin/users/${encodeURIComponent(uid)}/quota`, {
        method: "PUT",
        body: JSON.stringify({
          daily_lookup_max: lookupMax.trim() === "" ? null : Number(lookupMax),
          daily_suggest_max:
            suggestMax.trim() === "" ? null : Number(suggestMax),
          reason: reason.trim() || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", uid] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setEditing(false);
    },
  });

  const clearQuota = useMutation({
    mutationFn: () =>
      api(`/v1/admin/users/${encodeURIComponent(uid)}/quota`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", uid] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setEditing(false);
    },
  });

  const lookupUsed = quota.today.lookup_count_today;
  const lookupLimit = quota.effective.daily_lookup_max;
  const suggestUsed = quota.today.suggest_count_today;
  const suggestLimit = quota.effective.daily_suggest_max;
  const resetIn = Math.max(0, quota.today.quota_reset_at - Date.now());
  const resetHrs = Math.floor(resetIn / 3_600_000);
  const resetMins = Math.floor((resetIn % 3_600_000) / 60_000);

  return (
    <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-900">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Daily quota
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Resets in {resetHrs}h {resetMins}m (UTC midnight).{" "}
            {quota.override ? (
              <>
                Custom override by{" "}
                <span className="font-mono">{quota.override.set_by}</span>.
              </>
            ) : (
              "On default limits."
            )}
          </p>
        </div>
        {!editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {quota.override ? "Edit override" : "Set override"}
          </Button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <QuotaGauge
          label="Lookups today"
          used={lookupUsed}
          limit={lookupLimit}
        />
        <QuotaGauge
          label="Suggests today"
          used={suggestUsed}
          limit={suggestLimit}
        />
      </div>

      {quota.override?.reason && !editing && (
        <p className="mt-3 text-xs italic text-gray-500 dark:text-gray-500">
          Reason: {quota.override.reason}
        </p>
      )}

      {editing && (
        <div className="mt-4 space-y-3 rounded-md border border-gray-200 p-3 dark:border-gray-800">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Leave a field empty to use the env default. Use 0 to lock the bucket
            for the rest of the day.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="quota-lookup">Daily lookup max</Label>
              <Input
                id="quota-lookup"
                type="number"
                min={0}
                value={lookupMax}
                onChange={(e) => setLookupMax(e.target.value)}
                placeholder={`default ${lookupLimit}`}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="quota-suggest">Daily suggest max</Label>
              <Input
                id="quota-suggest"
                type="number"
                min={0}
                value={suggestMax}
                onChange={(e) => setSuggestMax(e.target.value)}
                placeholder={`default ${suggestLimit}`}
                className="mt-1.5"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="quota-reason">Reason (optional)</Label>
            <Input
              id="quota-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. trusted partner / suspected abuse"
              className="mt-1.5"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              isLoading={setQuota.isPending}
              onClick={() => setQuota.mutate()}
            >
              Save
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            {quota.override && (
              <Button
                variant="destructive"
                isLoading={clearQuota.isPending}
                onClick={() => clearQuota.mutate()}
              >
                Clear override
              </Button>
            )}
          </div>
          {(setQuota.error || clearQuota.error) && (
            <p className="text-xs text-red-600 dark:text-red-500">
              {((setQuota.error ?? clearQuota.error) as Error).message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function QuotaGauge({
  label,
  used,
  limit,
}: {
  label: string;
  used: number;
  limit: number;
}) {
  const ratio = limit > 0 ? used / limit : 0;
  const variant: "default" | "warning" | "error" =
    ratio >= 1 ? "error" : ratio >= 0.8 ? "warning" : "default";
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-xs">
        <span className="text-gray-500 dark:text-gray-500">{label}</span>
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {fmtNumber(used)} / {fmtNumber(limit)}
        </span>
      </div>
      <ProgressBar value={used} max={Math.max(limit, 1)} variant={variant} />
    </div>
  );
}

/**
 * Recent searches for this uid. Cursor-paginated by created_at; the
 * "Older" button steps back through pages. Server caps page size at 200;
 * we ask for 25 per page to keep the modal scroll manageable.
 */
function UserSearches({ uid }: { uid: string }) {
  const [pages, setPages] = useState<SearchLogRow[][]>([]);
  const [cursor, setCursor] = useState<number | null>(null);

  const params = new URLSearchParams({ limit: "25" });
  if (cursor) params.set("before", String(cursor));
  const path = `/v1/admin/users/${encodeURIComponent(uid)}/searches?${params.toString()}`;

  const { data, isLoading, error, isFetching } = useQuery<SearchesResponse>({
    queryKey: ["admin-user-searches", uid, cursor],
    queryFn: () => api<SearchesResponse>(path),
  });

  useEffect(() => {
    if (data) {
      setPages((prev) => {
        // First page (no cursor) → reset; subsequent pages → append.
        if (cursor === null) return [data.items];
        return [...prev, data.items];
      });
    }
  }, [data, cursor]);

  const all = pages.flat();

  return (
    <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
        Recent searches
      </h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
        Every /v1/foods/lookup call this user has made. Use this to debug
        "lookup never resolves" reports and to spot abuse patterns
        (high LLM count + nonsense queries).
      </p>

      {error && (
        <Callout title="Failed to load searches" variant="error">
          {(error as Error).message}
        </Callout>
      )}

      {isLoading && all.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">Loading…</p>
      ) : all.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No searches yet.</p>
      ) : (
        <TableRoot className="mt-3">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell>Query</TableHeaderCell>
                  <TableHeaderCell>Result</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell>LLM?</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {all.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">
                      {fmtRelative(r.created_at)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.query_raw}
                    </TableCell>
                    <TableCell>
                      {r.resolved ? (
                        <Badge variant="success">resolved</Badge>
                      ) : (
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant="warning">miss</Badge>
                          {r.miss_reason && (
                            <span
                              className="font-mono text-[10px] text-gray-500 dark:text-gray-500"
                              title={describeMissReason(r.miss_reason)}
                            >
                              {r.miss_reason}
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.source ?? "—"}</TableCell>
                    <TableCell>
                      {r.llm_called ? (
                        <Badge variant="warning">yes</Badge>
                      ) : (
                        <Badge variant="neutral">no</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableRoot>
      )}

      {data?.next_cursor != null && (
        <div className="mt-3">
          <Button
            variant="secondary"
            disabled={isFetching}
            onClick={() => setCursor(data.next_cursor)}
          >
            Older
          </Button>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "err";
}) {
  const valueColor =
    tone === "err"
      ? "text-red-600 dark:text-red-500"
      : tone === "ok"
        ? "text-emerald-600 dark:text-emerald-500"
        : "text-gray-900 dark:text-gray-50";
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-500">
        {label}
      </p>
      <p className={`mt-1 text-base font-semibold tabular-nums ${valueColor}`}>
        {value}
      </p>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  from,
  to,
  busy,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  from: number;
  to: number;
  busy: boolean;
  onChange: (p: number) => void;
}) {
  const windowSize = 5;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + windowSize - 1);
  if (end - start + 1 < windowSize) {
    start = Math.max(1, end - windowSize + 1);
  }
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-col items-center justify-between gap-3 sm:flex-row"
    >
      <p className="text-xs text-gray-500 dark:text-gray-500">
        Showing{" "}
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {fmtNumber(from)}–{fmtNumber(to)}
        </span>{" "}
        of{" "}
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {fmtNumber(total)}
        </span>
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="secondary"
          disabled={page <= 1 || busy}
          onClick={() => onChange(page - 1)}
          className="!px-2 !py-1"
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
        </Button>
        {pages.map((p) => (
          <Button
            key={p}
            variant={p === page ? "primary" : "secondary"}
            disabled={busy}
            onClick={() => onChange(p)}
            className="!min-w-[36px] !px-2 !py-1 !text-xs tabular-nums"
            aria-current={p === page ? "page" : undefined}
          >
            {p}
          </Button>
        ))}
        <Button
          variant="secondary"
          disabled={page >= totalPages || busy}
          onClick={() => onChange(page + 1)}
          className="!px-2 !py-1"
          aria-label="Next page"
        >
          <ChevronRight size={14} />
        </Button>
      </div>
    </nav>
  );
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-10 w-full animate-pulse rounded-md bg-gray-100 dark:bg-gray-900"
        />
      ))}
    </div>
  );
}
